/**
 * Phase classification system - FOR UI DISPLAY ONLY
 *
 * IMPORTANT: This file is for CLIENT-SIDE DISPLAY/UI purposes only.
 * It does NOT set the Phase tag in drill data.
 *
 * Phase tag assignment is done ONLY in Phase 3 rules in processDrillTranscripts.ts
 * Valid Phase values: Phase 1, Phase 2, Phase 3
 */

export type PhaseClassification = 'P1-APT' | 'P1-SB' | 'P2' | 'P3' | 'Not Assessed';

export interface PhaseClassificationData {
  phase: PhaseClassification;
  description: string;
  color: string;
  bgColor: string;
}

/**
 * Get phase classification data for UI display
 *
 * NOTE: This is for displaying posture classification UI elements.
 * This does NOT affect drill data Phase tags.
 */
export const getPhaseClassificationData = (phase: PhaseClassification): PhaseClassificationData => {
  switch (phase) {
    case 'P1-APT':
      return {
        phase: 'P1-APT',
        description: 'Phase 1 - Anterior Pelvic Tilt',
        color: 'text-status-warn',
        bgColor: 'bg-status-warn-surface'
      };
    case 'P1-SB':
      return {
        phase: 'P1-SB',
        description: 'Phase 1 - Swayback',
        color: 'text-status-deviation',
        bgColor: 'bg-status-deviation-surface'
      };
    case 'P2':
      return {
        phase: 'P2',
        description: 'Phase 2',
        color: 'text-accent',
        bgColor: 'bg-accent/10 border border-accent/30'
      };
    case 'P3':
      return {
        phase: 'P3',
        description: 'Phase 3',
        color: 'text-status-clear',
        bgColor: 'bg-status-clear-surface'
      };
    default:
      return {
        phase: 'Not Assessed',
        description: 'Not yet assessed',
        color: 'text-muted',
        bgColor: 'bg-surface'
      };
  }
};

/**
 * Determine phase classification from posture analysis data (authoritative server classify).
 *
 * IMPORTANT: This is for UI display/classification only.
 * It does NOT set the Phase tag in drill data.
 */
export async function determinePhaseClassification(analysisData: any): Promise<PhaseClassification> {
  if (!analysisData) return 'Not Assessed';

  const { apiFetch } = await import('@/lib/api-client');
  const result = await apiFetch<any>('/posture-analyses/classify', {
    method: 'POST',
    body: JSON.stringify({ analysisData, clientSex: 'Unknown' })
  }).catch(() => null);

  if (!result) return 'P2';

  if (result.pattern?.includes('Anterior Pelvic Tilt')) return 'P1-APT';
  if (result.pattern?.includes('Swayback')) return 'P1-SB';

  return 'P2';
}
